import { Bell, CheckCheck, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllRead,
  type InAppNotification,
} from '../src/hooks/queries/use-notifications';
import { useAuth } from '../src/hooks/useAuth';
import { formatDistanceToNow } from 'date-fns';

function timeAgo(iso: string) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

interface Props {
  onViewAll: () => void;
  onNavigate: (path: string) => void;
}

function NotificationItem({
  notif,
  onRead,
  onNavigate,
}: {
  notif: InAppNotification;
  onRead: (id: string) => void;
  onNavigate: (path: string) => void;
}) {
  const handleClick = () => {
    if (!notif.is_read) onRead(notif.id);
    if (notif.action_url) onNavigate(notif.action_url);
  };

  return (
    <div
      className={`flex items-start gap-2 px-4 py-3 hover:bg-muted/50 cursor-pointer border-b last:border-b-0 transition-colors ${
        !notif.is_read ? 'bg-primary/5' : ''
      }`}
      onClick={handleClick}
    >
      {!notif.is_read ? (
        <span className="mt-1.5 h-2 w-2 rounded-full bg-primary flex-shrink-0" />
      ) : (
        <span className="mt-1.5 h-2 w-2 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight truncate">{notif.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
        <p className="text-xs text-muted-foreground/70 mt-1">{timeAgo(notif.created_at)}</p>
      </div>
      {notif.action_url && (
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-1" />
      )}
    </div>
  );
}

export function NotificationBell({ onViewAll, onNavigate }: Props) {
  const { user } = useAuth();
  const userId = (user as any)?.id ?? (user as any)?.sub ?? null;
  const { data: notifications = [] } = useNotifications(userId);
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markAllRead } = useMarkAllRead();

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const recent = notifications.slice(0, 8);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-[360px] p-0"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">Notifications</span>
            {unreadCount > 0 && (
              <Badge variant="destructive" className="text-xs h-5 px-1.5">
                {unreadCount}
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                markAllRead();
              }}
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Notification list */}
        <div className="max-h-[400px] overflow-y-auto">
          {recent.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            recent.map((n) => (
              <NotificationItem
                key={n.id}
                notif={n}
                onRead={(id) => markRead(id)}
                onNavigate={onNavigate}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t">
          <button
            className="w-full text-center text-sm text-primary hover:text-primary/80 py-2.5 font-medium transition-colors"
            onClick={onViewAll}
          >
            View all notifications
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
